import { useState } from "react";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { toast } from "sonner";

import { useVehicleCard } from "@/hooks/useVehicleCard";
import { getLanguage } from "@/utils/languageUtils";
import { getCarCardStatuses, getStatusConfig } from "@/utils/statusUtils";
import {
  vehicleCardStatusLabels,
  vehicleCardUILabels,
  vehicleCardToastLabels,
} from "@/i18n";
import { VehicleCardStatus } from "@/types/enums";
import { AlertCircle, Loader2 } from "lucide-react";

interface CarCardStatusSelectorProps {
  initialStatus: string;
  onStatusChange: (status: string) => void;
  vehicleCardId: string;
}

export const CarCardStatusSelector = ({
  initialStatus,
  onStatusChange,
  vehicleCardId,
}: CarCardStatusSelectorProps) => {
  // Backend sends uppercase status (ACTIVE, INACTIVE, etc.)
  // Convert to label for display in dropdown (uses current language)
  const normalizedStatus = initialStatus.toUpperCase() as VehicleCardStatus;
  const labels = vehicleCardStatusLabels(getLanguage());
  const initialLabel = labels[normalizedStatus] || initialStatus;

  const [status, setStatus] = useState<string>(initialLabel);

  const { updateVehicleCardStatusAsync, isUpdatingVehicleCardStatus } =
    useVehicleCard();

  const handleStatusChange = async (swedishLabel: string) => {
    try {
      // Convert label back to technical enum value for backend
      // Find the enum value that corresponds to this label (uses current language)
      const currentLabels = vehicleCardStatusLabels(getLanguage());
      const technicalStatus = Object.entries(currentLabels).find(
        ([_, label]) => label === swedishLabel,
      )?.[0] as VehicleCardStatus | undefined;

      if (!technicalStatus) {
        throw new Error(`Invalid status label: ${swedishLabel}`);
      }

      await updateVehicleCardStatusAsync({
        vehicleCardId,
        newStatus: technicalStatus,
      });

      // Only update local state if the API call succeeds (keep label for display)
      setStatus(swedishLabel);
      onStatusChange(technicalStatus);

      const toastLabels = vehicleCardToastLabels(getLanguage());
      toast.success(toastLabels.statusUpdated, { description: `${toastLabels.statusUpdatedDescription} ${swedishLabel}` });
    } catch (error) {
      const toastLabels = vehicleCardToastLabels(getLanguage());
      toast.success(toastLabels.statusUpdateError, { description: toastLabels.statusUpdateErrorDescription });
    }
  };

  return (
    <div className="flex items-center gap-1 sm:gap-2">
      <AlertCircle className="h-4 w-4 text-muted-foreground" />
      <div>
        <div className="text-xs text-muted-foreground">Status</div>
        <Select
          value={status}
          onValueChange={handleStatusChange}
          disabled={isUpdatingVehicleCardStatus}
        >
          <SelectTrigger className="w-full sm:w-[180px]">
            {isUpdatingVehicleCardStatus ? (
              <div className="flex items-center gap-2">
                <Loader2 className="h-4 w-4 animate-spin" />
                <span>{vehicleCardUILabels(getLanguage()).updating}</span>
              </div>
            ) : (
              <SelectValue
                placeholder={vehicleCardUILabels(getLanguage()).selectStatus}
              />
            )}
          </SelectTrigger>
          <SelectContent>
            {getCarCardStatuses().map((statusConfig) => (
              <SelectItem key={statusConfig.value} value={statusConfig.label}>
                {statusConfig.label}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
      </div>
    </div>
  );
};
